import BackendService from "../../../backend/controller/BackendService";
import CharacterModel from "../../../characters/model/CharacterModel";
import DebugModel from "../../../debug/model/DebugModel";
import PlayerModel from "../../../player/model/PlayerModel";
import Conversation from "../../model/Conversation";
import ConversationFeedback from "../../model/ConversationFeedback";
import GameConversationMessage from "../../model/GameConversationMessage";
import LlmConversationMessage, { LlmMessageSender } from "../../model/LlmConversationMessage";
import ConversationState from "./ConversationState";

export default class EndingTurnConversationState extends ConversationState {
    /**
     * 
     * @param {string} chapterId
     * @param {Conversation} conversation 
     * @param {CharacterModel[]} characters 
     * @param {BackendService} backendService 
     * @param {PlayerModel} playerModel
     * @returns {Promise<ConversationState>}
     */
    async execute(chapterId, conversation, characters, backendService, playerModel) {
        try {
            // Generate feedback on conversation.
            const conversationFeedbackPromise = backendService.conversationFeedback({
                chapterId: chapterId,
                conversationId: conversation.id,
                conversationHistory: conversation.conversationHistoryForLLM,
            });

            // Generate last roleplay message.
            const roleplayMessagePromise = backendService.roleplayEndOfConversation({
                chapterId: chapterId,
                conversationId: conversation.id,
                phaseId: conversation.currentPhaseId,
                turnsInThisPhase: conversation.getTurnsForCurrentPhase(),
                conversationHistory: conversation.conversationHistoryForLLM,
                characters,
            });

            // For now we wait on both promises. As an optimalization step we could directly show the roleplay output if it is there.
            await Promise.all([conversationFeedbackPromise, roleplayMessagePromise]);

            // The back-end requires the assistant message to be a single string, or it gets confused.
            // For the game itself we want to keep the messages separate.
            const roleplayMessageOutput = await roleplayMessagePromise;
            const combinedRoleplayMessage = roleplayMessageOutput.characters.map(characterOutput => `${characterOutput.name}: ${characterOutput.dialogue}`).join('\n');
            conversation.addConversationHistory({
                llmConversationMessages: [new LlmConversationMessage(LlmMessageSender.ASSISTANT, combinedRoleplayMessage)],
                gameConversationMessages: roleplayMessageOutput.characters.map(characterOutput => new GameConversationMessage(characterOutput.name, characterOutput.dialogue, characterOutput.action)),
            });

            // Log the feedback.
            const conversationFeedbackOutput = await conversationFeedbackPromise;
            for (const feedbackObj of conversationFeedbackOutput.feedback) {
                conversation.addFeedback(new ConversationFeedback(feedbackObj.quote, feedbackObj.feedback));
            }

            // Actually end the conversation.
            conversation.end();

            // Log the feedback summary.
            if (ranj.DEBUG || DebugModel.debugContent) {
                console.log(`%c📘 Feedback summary:\n${conversationFeedbackOutput.summary}`, 'background: #ffda7d; font-size: 16px; color: black;');
            }
            conversation.feedbackSummary = conversationFeedbackOutput.summary;

            const nextState = null; // There is no next state.
            return Promise.resolve(nextState);
        } catch (error) {
            console.error(`[${EndingTurnConversationState.name}] Error: `, error);
            return Promise.reject(error);
        }
    }
}